// 'use client';

/**
 * @link https://github.com/mui/material-ui/blob/HEAD/examples/nextjs-with-typescript/src/Link.tsx
 * @link https://mui.com/material-ui/guides/routing/#next-js
 */
import MuiLink from '@mui/material/Link';
import clsx from 'clsx';
import * as React from 'react';

import type { LinkProps as MuiLinkProps } from '@mui/material/Link';
import type { LinkProps as NextLinkProps } from 'next/link';
import { usePathname } from 'next/navigation';
import { Anchor } from './Anchor';
import { NextLinkComposed } from './NextLinkCompose';
import type { NextLinkComposedProps } from './NextLinkCompose';

// Add support for the sx prop for consistency with the other branches.
// const Anchor = styled( 'a' )( {} );

// export interface NextLinkComposedProps
//   extends
//     Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>,
//     Omit<
//       NextLinkProps,
//       'href' | 'as' | 'onClick' | 'onMouseEnter' | 'onTouchStart'
//     >
// {
//   to: NextLinkProps['href'];
//
//   linkAs?: NextLinkProps['as'];
// }
//
// export const NextLinkComposed = React.forwardRef<
//   HTMLAnchorElement,
//   NextLinkComposedProps
// >( function NextLinkComposed(props, ref) {
//   const { to, linkAs, replace, scroll, shallow, prefetch, locale, ...other } = props;
//
//   return (
//     <NextLink
//       href={to}
//       prefetch={prefetch}
//       as={linkAs}
//       replace={replace}
//       scroll={scroll}
//       shallow={shallow}
//       passHref
//       locale={locale}
//       legacyBehavior
//     >
//       <Anchor ref={ref} {...other} />
//     </NextLink>
//   );
// } );

export type LinkProps =
  & {
    activeClassName?: string;
    as?: NextLinkProps['as'];
    href: NextLinkProps['href'];
    linkAs?: NextLinkProps['as']; // Useful when the as prop is shallow by styled().
    noLinkStyle?: boolean;
  }
  & Omit<NextLinkComposedProps, 'to' | 'linkAs' | 'href'>
  & Omit<MuiLinkProps, 'href'>;

// A styled version of the Next.js LinkBox component:
// https://nextjs.org/docs/api-reference/next/link

/**
 * registers if this link is used to href internally the next app[lang]
 * or if used to href sources outside the next app[lang]
 * returns either a mui/link extending next/link
 * or returns a mui/link extending a html anchor element
 */
// export type { LinkProps } from 'next/link';

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  function Link(props, ref) {
    const {
      activeClassName = 'active',
      as,
      className: classNameProps,
      href,
      linkAs: linkAsProp,
      locale,
      noLinkStyle,
      prefetch,
      replace,
      role, // LinkBox don't have roles.
      scroll,
      shallow,
      ...other
    } = props;

    const linkPathname = typeof href === 'string' ? href : href.pathname ?? '';
    const pathname = usePathname();
    const className = clsx( classNameProps, {
      [activeClassName]: pathname === linkPathname && activeClassName,
    } );

    const isExternal = typeof href === 'string'
      && (href.startsWith( 'http' ) || href.startsWith( 'mailto:' ));

    if (isExternal) {
      if (noLinkStyle) {
        return (
          <Anchor
            className={className}
            href={href}
            target="_blank"
            ref={ref}
            {...other}
          />
        );
      }

      return (
        <MuiLink
          className={className}
          href={href}
          target="_blank"
          ref={ref}
          {...other}
        />
      );
    }

    const linkAs = linkAsProp || as;
    const nextjsProps = {
      to: href,
      linkAs,
      replace,
      scroll,
      shallow,
      prefetch,
      locale,
    };

    if (noLinkStyle) {
      return (
        <NextLinkComposed
          className={className}
          ref={ref}
          {...nextjsProps}
          {...other}
        />
      );
    }

    return (
      <MuiLink
        component={NextLinkComposed}
        className={className}
        ref={ref}
        {...nextjsProps}
        {...other}
      />
    );
  }
);
